import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelRadio as _vModelRadio, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" }
const _hoisted_2 = { class: "bg-blue-50 p-4 rounded-lg border border-blue-200" }
const _hoisted_3 = { class: "flex space-x-4" }
const _hoisted_4 = { class: "flex items-center" }
const _hoisted_5 = { class: "flex items-center" }
const _hoisted_6 = {
  key: 0,
  class: "bg-gray-50 p-4 rounded-lg border"
}
const _hoisted_7 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_8 = { class: "relative" }
const _hoisted_9 = { class: "relative" }
const _hoisted_10 = ["value"]
const _hoisted_11 = {
  key: 1,
  class: "bg-gray-50 p-4 rounded-lg border"
}
const _hoisted_12 = { class: "mb-6" }
const _hoisted_13 = ["value"]
const _hoisted_14 = { class: "mb-6" }
const _hoisted_15 = { class: "flex items-center justify-between mb-4" }
const _hoisted_16 = { class: "text-sm text-black" }
const _hoisted_17 = { class: "grid grid-cols-6 gap-3" }
const _hoisted_18 = ["for"]
const _hoisted_19 = ["id", "onUpdate:modelValue", "onBlur"]
const _hoisted_20 = {
  key: 0,
  class: "mt-2 text-sm text-red-600"
}
const _hoisted_21 = { class: "flex justify-end gap-3 pt-4 border-t border-gray-200" }
const _hoisted_22 = ["disabled"]
const _hoisted_23 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}
const _hoisted_24 = {
  key: 1,
  class: "fas fa-plus mr-2"
}

import { ref, computed, watch, reactive } from 'vue'
import type { PackageFormData, PackageItem } from '@/types/pack'

interface Props {
  packagesExistants?: PackageItem[]
}

interface Emits {
  (e: 'submit', data: PackageFormData): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PackageForm',
  props: {
    packagesExistants: { default: () => [] }
  },
  emits: ["submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Mode de création
const creationMode = ref<'template' | 'custom'>('custom')

// Données pour les templates
const selectedNbTailles = ref('all')
const selectedTemplate = ref('')

// Paquets filtrés
const filteredPackages = ref<PackageItem[]>([])

// Filtrer les paquets selon le nombre de tailles sélectionné
const filterPackages = () => {
  selectedTemplate.value = '' // Réinitialiser la sélection
  
  if (selectedNbTailles.value === 'all') {
    filteredPackages.value = props.packagesExistants
  } else {
    const nbTailles = parseInt(selectedNbTailles.value)
    filteredPackages.value = props.packagesExistants.filter(pkg => pkg.nombreTailles === nbTailles)
  }
}

const formData = reactive<PackageFormData>({
  titre: null,
  p1: 0,
  p2: 0,
  p3: 0,
  p4: 0,
  p5: 0,
  p6: 0,
  p7: 0,
  p8: 0,
  p9: 0,
  p10: 0,
  p11: 0,
  p12: 0,
})

// Colonnes de quantités pour la création personnalisée
const selectedNombreTailles = ref<number | null>(null)
const quantityColumns = ref<(number | null)[]>(Array(12).fill(null))
const visibleQuantityColumns = computed(() =>
  quantityColumns.value.slice(0, selectedNombreTailles.value ?? 0)
)

const clearCustomQuantities = () => {
  quantityColumns.value = Array(12).fill(null)
}

const errors = reactive({
  titre: '',
  quantites: ''
})

const isSubmitting = ref(false)

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  // En mode template, il suffit d'avoir sélectionné un template
  if (creationMode.value === 'template') {
    return selectedTemplate.value !== ''
  }
  
  // En mode custom, toutes les quantités visibles doivent être renseignées
  if (creationMode.value === 'custom') {
    if (selectedNombreTailles.value === null) return false
    return quantityColumns.value
      .slice(0, selectedNombreTailles.value)
      .every(qty => Number.isInteger(qty) && Number(qty) > 0)
  }
  
  return true
})

// Charger un template existant
const loadTemplate = () => {
  if (selectedTemplate.value) {
    const pkg = props.packagesExistants.find(p => p.id.toString() === selectedTemplate.value)
    if (pkg) {
      formData.titre = pkg.titre || null
      selectedNombreTailles.value = pkg.nombreTailles
      // Remplir les quantités selon le template
      quantityColumns.value = [
        pkg.quantites[0] || 0,
        pkg.quantites[1] || 0,
        pkg.quantites[2] || 0,
        pkg.quantites[3] || 0,
        pkg.quantites[4] || 0,
        pkg.quantites[5] || 0,
        pkg.quantites[6] || 0,
        pkg.quantites[7] || 0,
        pkg.quantites[8] || 0,
        pkg.quantites[9] || 0,
        pkg.quantites[10] || 0,
        pkg.quantites[11] || 0,
      ]
    }
  }
}

// Fonction pour nettoyer une quantité quand l'utilisateur quitte le champ
const cleanQuantity = (index: number) => {
  const value = quantityColumns.value[index]
  if (value === null || value === undefined || isNaN(Number(value))) {
    quantityColumns.value[index] = null
  } else {
    const numericValue = Number(value)
    quantityColumns.value[index] = Number.isInteger(numericValue) && numericValue > 0
      ? numericValue
      : null
  }
}

const handleSubmit = async () => {
  if (!isFormValid.value) return

  isSubmitting.value = true
  
  try {
    // Créer l'objet avec le titre et les 12 positions de quantités
    const dataToSubmit = {
      titre: formData.titre || null,
      p1: quantityColumns.value[0] || 0,
      p2: quantityColumns.value[1] || 0,
      p3: quantityColumns.value[2] || 0,
      p4: quantityColumns.value[3] || 0,
      p5: quantityColumns.value[4] || 0,
      p6: quantityColumns.value[5] || 0,
      p7: quantityColumns.value[6] || 0,
      p8: quantityColumns.value[7] || 0,
      p9: quantityColumns.value[8] || 0,
      p10: quantityColumns.value[9] || 0,
      p11: quantityColumns.value[10] || 0,
      p12: quantityColumns.value[11] || 0,
    }
    
    emit('submit', dataToSubmit)
    
    resetForm()
  } catch (error) {
    console.error('Erreur lors de la création:', error)
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  formData.titre = null
  selectedNombreTailles.value = null
  quantityColumns.value = Array(12).fill(null)
  selectedTemplate.value = ''
  selectedNbTailles.value = 'all'
  creationMode.value = 'custom'
  errors.titre = ''
  errors.quantites = ''
}

// Initialiser les paquets filtrés
watch(() => props.packagesExistants, () => {
  filterPackages()
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[19] || (_cache[19] = _createElementVNode("h3", { class: "text-lg font-medium text-black mb-4" }, "Créer un nouveau paquet", -1)),
    _createElementVNode("form", {
      onSubmit: _withModifiers(handleSubmit, ["prevent"]),
      class: "space-y-6"
    }, [
      _createElementVNode("div", _hoisted_2, [
        _cache[8] || (_cache[8] = _createElementVNode("h4", { class: "text-lg font-semibold text-blue-900 mb-4" }, "Mode de création", -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("label", _hoisted_4, [
            _withDirectives(_createElementVNode("input", {
              type: "radio",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((creationMode).value = $event)),
              value: "template",
              class: "mr-2"
            }, null, 512), [
              [_vModelRadio, creationMode.value]
            ]),
            _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-sm font-medium text-black" }, "Utiliser un paquet existant", -1))
          ]),
          _createElementVNode("label", _hoisted_5, [
            _withDirectives(_createElementVNode("input", {
              type: "radio",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((creationMode).value = $event)),
              value: "custom",
              class: "mr-2"
            }, null, 512), [
              [_vModelRadio, creationMode.value]
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-sm font-medium text-black" }, "Créer mon propre paquet", -1))
          ])
        ])
      ]),
      (creationMode.value === 'template')
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _cache[13] || (_cache[13] = _createElementVNode("h4", { class: "text-lg font-semibold text-black mb-4" }, "Paquet Existant", -1)),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", null, [
                _cache[10] || (_cache[10] = _createElementVNode("label", {
                  for: "paquet-taille",
                  class: "block text-sm font-medium text-black mb-2"
                }, " Combien avez-vous de tailles ? ", -1)),
                _createElementVNode("div", _hoisted_8, [
                  _withDirectives(_createElementVNode("select", {
                    id: "paquet-taille",
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedNbTailles).value = $event)),
                    onChange: filterPackages,
                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  }, _cache[9] || (_cache[9] = [
                    _createStaticVNode("<option value=\"0\">0</option><option value=\"1\">1</option><option value=\"2\">2</option><option value=\"3\">3</option><option value=\"4\">4</option><option value=\"5\">5</option><option value=\"6\">6</option><option value=\"7\">7</option><option value=\"8\">8</option><option value=\"9\">9</option><option value=\"10\">10</option><option value=\"11\">11</option><option value=\"12\">12</option><option value=\"all\">Afficher tout</option>", 14)
                  ]), 544), [
                    [_vModelSelect, selectedNbTailles.value]
                  ])
                ])
              ]),
              _createElementVNode("div", null, [
                _cache[12] || (_cache[12] = _createElementVNode("label", {
                  for: "paquet-liste",
                  class: "block text-sm font-medium text-black mb-2"
                }, " Liste des paquets ", -1)),
                _createElementVNode("div", _hoisted_9, [
                  _withDirectives(_createElementVNode("select", {
                    id: "paquet-liste",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedTemplate).value = $event)),
                    onChange: loadTemplate,
                    class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  }, [
                    _cache[11] || (_cache[11] = _createElementVNode("option", { value: "" }, "-", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPackages.value, (pkg) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: pkg.id,
                        value: pkg.id
                      }, _toDisplayString(pkg.titre || 'Sans titre') + " - " + _toDisplayString(pkg.quantites.join('-')), 9, _hoisted_10))
                    }), 128))
                  ], 544), [
                    [_vModelSelect, selectedTemplate.value]
                  ])
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      (creationMode.value === 'custom')
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _cache[18] || (_cache[18] = _createElementVNode("h4", { class: "text-lg font-semibold text-black mb-4" }, "Création Personnalisée", -1)),
            _createElementVNode("div", _hoisted_12, [
              _cache[15] || (_cache[15] = _createElementVNode("label", {
                for: "nombre-tailles",
                class: "block text-sm font-medium text-black mb-2"
              }, [
                _createTextVNode(" Définir le nombre de taille dans le paquet "),
                _createElementVNode("span", { class: "text-red-500" }, "*")
              ], -1)),
              _withDirectives(_createElementVNode("select", {
                id: "nombre-tailles",
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedNombreTailles).value = $event)),
                onChange: clearCustomQuantities,
                class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              }, [
                _cache[14] || (_cache[14] = _createElementVNode("option", {
                  value: null,
                  disabled: ""
                }, "Sélectionnez un nombre de tailles", -1)),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (nombreTailles) => {
                  return _createElementVNode("option", {
                    key: nombreTailles,
                    value: nombreTailles
                  }, _toDisplayString(nombreTailles), 9, _hoisted_13)
                }), 64))
              ], 544), [
                [
                  _vModelSelect,
                  selectedNombreTailles.value,
                  void 0,
                  { number: true }
                ]
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _cache[16] || (_cache[16] = _createElementVNode("label", {
                for: "titre",
                class: "block text-sm font-medium text-black mb-2"
              }, " Titre du paquet ", -1)),
              _withDirectives(_createElementVNode("input", {
                id: "titre",
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.titre) = $event)),
                type: "text",
                placeholder: "Ex: Pack Standard, 1-1-1-1-1 (optionnel)",
                class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              }, null, 512), [
                [_vModelText, formData.titre]
              ])
            ]),
            _createElementVNode("div", null, [
              _createElementVNode("div", _hoisted_15, [
                _cache[17] || (_cache[17] = _createElementVNode("label", { class: "block text-sm font-medium text-black" }, [
                  _createTextVNode(" Quantités par taille "),
                  _createElementVNode("span", { class: "text-red-500" }, "*")
                ], -1)),
                _createElementVNode("div", _hoisted_16, " Colonnes p1 à p" + _toDisplayString(selectedNombreTailles.value || 0), 1)
              ]),
              _createElementVNode("div", _hoisted_17, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleQuantityColumns.value, (quantite, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "text-center"
                  }, [
                    _createElementVNode("label", {
                      for: `p${index + 1}`,
                      class: "block text-xs font-medium text-black mb-1"
                    }, " p" + _toDisplayString(index + 1), 9, _hoisted_18),
                    _createElementVNode("div", {
                      class: _normalizeClass(["px-2 py-1 rounded-md border", quantite > 0 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'])
                    }, [
                      _withDirectives(_createElementVNode("input", {
                        id: `p${index + 1}`,
                        "onUpdate:modelValue": ($event: any) => ((quantityColumns.value[index]) = $event),
                        type: "number",
                        min: "1",
                        step: "1",
                        class: _normalizeClass(["w-full text-center bg-transparent border-none outline-none text-sm font-medium", quantite > 0 ? 'text-green-700' : 'text-black']),
                        onBlur: ($event: any) => (cleanQuantity(index))
                      }, null, 42, _hoisted_19), [
                        [
                          _vModelText,
                          quantityColumns.value[index],
                          void 0,
                          { number: true }
                        ]
                      ])
                    ], 2)
                  ]))
                }), 128))
              ]),
              (errors.quantites)
                ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(errors.quantites), 1))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_21, [
        _createElementVNode("button", {
          type: "button",
          onClick: resetForm,
          class: "px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-black bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        }, " Réinitialiser "),
        _createElementVNode("button", {
          type: "submit",
          disabled: !isFormValid.value || isSubmitting.value,
          class: "px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        }, [
          (isSubmitting.value)
            ? (_openBlock(), _createElementBlock("i", _hoisted_23))
            : (_openBlock(), _createElementBlock("i", _hoisted_24)),
          _createTextVNode(" " + _toDisplayString(isSubmitting.value ? 'Création...' : 'Créer le paquet'), 1)
        ], 8, _hoisted_22)
      ])
    ], 32)
  ]))
}
}

})